import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'sm' | 'md' | 'lg';
  elevation?: 'flat' | 'raised' | 'elevated';
  interactive?: boolean;
  children: ReactNode;
}

const paddingStyles = {
  none: '',
  sm: 'p-3',
  md: 'p-6',
  lg: 'p-8',
} as const;

const elevationStyles = {
  flat: '',
  raised: 'shadow-elev-2',
  elevated: 'shadow-elev-3',
} as const;

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { padding = 'md', elevation = 'flat', interactive = false, className, children, ...rest },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl border border-border-subtle bg-bg-surface',
          'transition-[transform,box-shadow,border-color] duration-fast',
          paddingStyles[padding],
          elevationStyles[elevation],
          interactive &&
            'hover:shadow-elev-2 cursor-pointer hover:-translate-y-0.5 hover:border-brand-400',
          className
        )}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn('mb-1 text-base font-semibold text-text-primary', className)}>{children}</h3>
  );
}

export function CardDescription({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <p className={cn('text-sm text-text-secondary', className)}>{children}</p>;
}
